import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import ConfirmDialog from '../components/ConfirmDialog';
import AdminUserRow from './AdminUserRow';
import { SORT_OPTIONS, groupByPhone, compareGroups } from './AdminUsersPage.helpers';

const th = {
  padding: '10px', textAlign: 'left', fontSize: '0.8rem',
  color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap',
};

export default function AdminUsersPage() {
  const { user, getIdToken } = useAuth();
  const [groups, setGroups] = useState([]);
  const [adminCount, setAdminCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [sortKey, setSortKey] = useState('latestCreatedAt');
  const [mode, setMode] = useState('admin-only'); // 'admin-only' | 'search'
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingToggle, setPendingToggle] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) { setError('로그인이 필요합니다'); setLoading(false); return; }
      const res = await fetch(`${AWS_CONFIG.apiUrl}/admin/users`, { headers: { Authorization: token } });
      if (!res.ok) {
        setError(`조회 실패 (${res.status})`);
      } else {
        const data = await res.json();
        const users = data.users || [];
        setGroups(groupByPhone(users));
        setAdminCount(data.adminCount || 0);
        setTotalCount(users.length);
      }
    } catch (err) {
      setError(`네트워크: ${err.message}`);
    }
    setLoading(false);
  }, [getIdToken]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function doToggleAdmin(targetUser) {
    const action = targetUser.isAdmin ? 'revoke-admin' : 'grant-admin';
    setBusy(targetUser.sub); setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/admin/users/${targetUser.sub}/${action}`, {
        method: 'POST', headers: { Authorization: token, 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.error === 'last admin cannot be removed') setError('마지막 관리자는 해제할 수 없습니다 (락아웃 방지).');
        else if (body.error === 'cannot revoke your own admin') setError('자기 자신의 관리자 권한은 해제할 수 없습니다.');
        else setError(`실패: ${body.error || res.status}`);
      } else {
        await fetchUsers();
      }
    } catch (err) {
      setError(`네트워크 오류: ${err.message}`);
    }
    setBusy(null); setPendingToggle(null);
  }

  async function handleDelete(targetUser) {
    setBusy(targetUser.sub); setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/admin/users/${targetUser.sub}`, {
        method: 'DELETE', headers: { Authorization: token },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.error === 'cannot delete your own account') setError('자기 자신의 계정은 삭제할 수 없습니다.');
        else if (body.error === 'cannot delete the last admin') setError('마지막 관리자는 삭제할 수 없습니다 (락아웃 방지).');
        else setError(`삭제 실패: ${body.error || res.status}`);
      } else {
        await fetchUsers();
      }
    } catch (err) {
      setError(`네트워크 오류: ${err.message}`);
    }
    setBusy(null); setPendingDelete(null);
  }

  const callerSub = user?.userId || user?.username; // best-effort self detection
  const callerEmail = user?.email;

  const filtered = groups
    .map(g => {
      if (mode === 'admin-only') {
        const admins = g.members.filter(m => m.isAdmin);
        if (admins.length === 0) return null;
        return { ...g, members: admins };
      }
      return g;
    })
    .filter(Boolean)
    .filter(g => {
      if (!search) return true;
      const q = search.toLowerCase();
      return g.members.some(m =>
        (m.email || '').toLowerCase().includes(q)
        || (m.name || '').toLowerCase().includes(q)
        || (m.phoneNumber || '').includes(q));
    });

  const sortedGroups = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => -compareGroups(a, b, sortKey));
    return arr;
  }, [filtered, sortKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSelfUser = (u) =>
    (callerEmail && u.email === callerEmail) || (callerSub && u.sub === callerSub);

  return (
    <div style={{ padding: 20 }}>
      <h1>관리자 권한</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 6 }}>
        {mode === 'admin-only' ? `관리자 ${adminCount}명 / 전체 ${totalCount}명` : `전체 ${totalCount}명 중 검색`}
      </p>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 0, marginBottom: 14 }}>
        💡 기본은 관리자 명단만 표시됩니다. 새 관리자를 추가하려면 우측 버튼으로 검색 모드를 여세요. 같은 전화번호로 인증된 계정은 한 그룹으로 묶입니다.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={() => { setMode(mode === 'admin-only' ? 'search' : 'admin-only'); setSearch(''); }}
          style={{
            padding: '8px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
            background: mode === 'search' ? '#fff8e6' : 'var(--bg)',
            color: 'var(--text)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
          }}
        >
          {mode === 'admin-only' ? '🔍 새 관리자 추가' : '✖ 검색 닫기 (관리자만 보기)'}
        </button>
        {mode === 'search' && (
          <>
            <input
              className="input"
              placeholder="이메일·이름·전화번호 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 360, flex: 1, minWidth: 220 }}
            />
            <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              정렬
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                  background: 'var(--bg)', color: 'var(--text)', fontSize: '0.85rem',
                }}
              >
                {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </label>
          </>
        )}
      </div>

      {error && (
        <div className="card" style={{
          padding: 12, marginBottom: 16, borderColor: '#ff5252', background: 'rgba(255,82,82,0.08)',
          color: '#ff5252', fontSize: '0.9rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} aria-label="닫기" style={{
            background: 'none', border: 'none', color: '#ff5252', fontSize: '1.2rem', lineHeight: 1, cursor: 'pointer',
          }}>×</button>
        </div>
      )}

      {loading && <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>}

      {!loading && !error && sortedGroups.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: 720 }}>
            <thead>
              <tr style={{ background: 'var(--surface-elev)' }}>
                <th style={th}>이메일 / 이름</th>
                <th style={th}>로그인 방식</th>
                <th style={th}>전화번호</th>
                <th style={th}>가입일</th>
                <th style={{ ...th, textAlign: 'center' }}>권한</th>
                <th style={{ ...th, textAlign: 'right' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((group, gi) => {
                const multi = !!(group.phone && group.members.length > 1);
                return (
                  <React.Fragment key={group.phone || `orphan-${gi}`}>
                    {multi && (
                      <tr>
                        <td colSpan={6} style={{
                          padding: '8px 10px', fontSize: '0.8rem', color: '#7a5c00',
                          background: '#fff8e6', borderBottom: '1px solid var(--border)',
                        }}>
                          📱 동일 전화번호 그룹 · {group.phone} · {group.members.length}개 계정
                        </td>
                      </tr>
                    )}
                    {group.members.map(u => (
                      <AdminUserRow
                        key={u.sub}
                        user={u}
                        multi={multi}
                        busy={busy}
                        isSelf={isSelfUser(u)}
                        onToggle={setPendingToggle}
                        onDelete={setPendingDelete}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && sortedGroups.length === 0 && (
        <p style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>일치하는 사용자가 없습니다</p>
      )}

      <ConfirmDialog
        open={!!pendingToggle}
        title={pendingToggle?.isAdmin ? '관리자 권한을 해제하시겠습니까?' : '관리자 권한을 부여하시겠습니까?'}
        message={pendingToggle
          ? `${pendingToggle.email || pendingToggle.name || pendingToggle.sub.slice(0, 8)} 계정의 관리자 권한을 ${pendingToggle.isAdmin ? '해제' : '부여'}합니다.`
          : ''}
        confirmLabel={pendingToggle?.isAdmin ? '해제' : '부여'}
        cancelLabel="취소"
        danger={!!pendingToggle?.isAdmin}
        onConfirm={() => doToggleAdmin(pendingToggle)}
        onCancel={() => setPendingToggle(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="계정을 삭제하시겠습니까?"
        message={pendingDelete ? `${pendingDelete.email || pendingDelete.name || pendingDelete.sub.slice(0, 8)} 계정을 Cognito에서 삭제합니다.\n\n• 이 사용자는 즉시 로그인할 수 없게 됩니다\n• 예약 이력(Bookings)·고객 프로필(UserProfiles)은 보존됩니다 (이름·전화로 매칭 가능)\n• 되돌릴 수 없습니다 — 같은 사용자가 재가입하려면 새로 가입해야 합니다` : ''}
        confirmLabel="삭제"
        cancelLabel="취소"
        danger={true}
        requireTypedConfirmation={pendingDelete?.email || pendingDelete?.name || pendingDelete?.sub?.slice(0, 8)}
        onConfirm={() => handleDelete(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
